import React from 'react'

import { useI18n } from '@thg-commerce/enterprise-core'
import { Modal } from '@thg-commerce/gravity-elements'
import { ProductDescriptionAccordion } from '@thg-commerce/gravity-patterns'

import {
  BundleSku,
  MAX_QUANTITY_SELECTION,
} from '@thg-commerce/widget-build-your-own-bundle/src/BuildYourOwnBundle'

import {
  CloseIcon,
  IconContainer,
  InfoIconButton,
  OutOfStock,
  OutOfStockSubtitle,
  OutOfStockTitle,
  QuantityWrapper,
  StyledIconInfo,
  StyledProductBlock,
  StyledQuantitySelector,
  SubTitle,
  Title,
} from './styles'

import { BundleProduct } from './ResponsiveBuildYourOwnBundleProducts'

import {
  computeMaxQuantity,
  handleProductSelection,
  handleUpdateQuantity,
  INITIAL_QUANTITY,
  removeProduct,
} from './helpers'

export interface BundleProductBlockProps {
  product: BundleSku
  showProductReviews?: boolean
  productRRP?: boolean
  enableQuantitySelector?: boolean
  maxProductsPerCategory: number
  showPrices?: boolean
  inStock?: boolean
  numberOfProductsInSelectedCategory: number
  showInfoButton?: boolean
  selectedBundleProducts: BundleProduct[]
  categoryId: string
  setSelectedBundleProducts: (
    callback: (currentProducts: BundleProduct[]) => BundleProduct[],
  ) => void
  availableBundleQuantitySelection: number
}

export const BundleProductCard = (props: BundleProductBlockProps) => {
  const i18n = useI18n()

  const [openInfoModal, setOpenInfoModal] = React.useState(false)

  const [selectedQuantity, setSelectedQuantity] = React.useState<number>(
    INITIAL_QUANTITY,
  )

  const isProductSelected = props.selectedBundleProducts.some(
    (selectedProduct) => selectedProduct.sku === props.product.sku,
  )

  const productsInCategory = props.selectedBundleProducts.filter(
    (selectedProduct) => {
      return selectedProduct.categoryId === props.categoryId
    },
  )

  const maxBundleQuantityReached = props.availableBundleQuantitySelection === 0

  const computedMaxQuantity = computeMaxQuantity(
    props.product.maxPerOrder || MAX_QUANTITY_SELECTION,
    props.maxProductsPerCategory,
    props.numberOfProductsInSelectedCategory,
    selectedQuantity,
    props.availableBundleQuantitySelection,
  )

  const i18nText = {
    reviewsStarsLabel: i18n('reviews.stars.label'),
    reviewsLabel: i18n('reviews.reviews.label'),
    productInfoTitle: i18n('product.description.accordion.title'),
    productInfoUseAccordion: i18n('product.description.accordion.useaccordion'),
    soldOutText: i18n('general.productsoldout.text'),
    backInStockSoon: i18n('widget.buildyourbundle.backinstocksoon.text'),
    productCategoryTitle: i18n('product.content.productdetails.title.text'),
    closeAriaLabel: i18n('general.modal.close.button.arialabel'),
    closeLabel: i18n('general.modal.close.button.label'),
    productInfoAriaLabel: i18n('product.description.accordion.title'),
    quantitySelectorTexts: {
      decreaseButtonLabel: i18n('general.quantity.decrease'),
      increaseButtonLabel: i18n('general.quantity.increase'),
      quantityLabel: i18n('general.quantity.text'),
    },
    alternateImageText: i18n('widget.buildyourownbundle.alternateImage.text'),
  }

  const quantitySelector = {
    i18nText: i18nText.quantitySelectorTexts,
    maxValue: computedMaxQuantity,
    minValue: 1,
    editable: true,
    quantity: selectedQuantity,
  }

  const itemsWithTitle =
    props.product.productDescription?.items.map((item) => ({
      ...item,
      title: i18n(`product.content.${item.titleKey}.title.text`),
    })) || []

  const detailsWithTitle =
    props.product.productDescription?.details.map((item) => ({
      ...item,
      title: i18n(`product.content.${item.titleKey}.title.text`),
    })) || []

  const screenReaderReviewText = ({ score, total }) =>
    `${score} ${i18nText.reviewsStarsLabel} ${total} ${i18nText.reviewsLabel}`

  const productImages = {
    largeProduct:
      props.product.images[0]?.largeProduct ||
      props.product.product.images[0]?.largeProduct ||
      '',
  }

  const isQuantitySelectorEnabled =
    props.enableQuantitySelector &&
    isProductSelected &&
    props.maxProductsPerCategory > 1

  return (
    <div style={{ position: 'relative', height: '100%' }}>
      <StyledProductBlock
        url={''}
        button={{
          title: '',
          disabled: true,
          disableHref: true,
        }}
        disableUrl={true}
        review={
          props.showProductReviews && props.product.product?.reviews
            ? {
                starRating: props.product.product.reviews.averageScore,
                numberOfReviews: props.product.product.reviews.total,
                screenReaderOnlyText: screenReaderReviewText({
                  score: props.product.product.reviews.averageScore,
                  total: props.product.product.reviews.total,
                }),
              }
            : null
        }
        image={{
          urls: productImages,
          altText: `${props.product.title} ${i18nText.alternateImageText}`,
          isAmp: false,
        }}
        price={{
          price: {
            defaultPrice:
              props.showPrices && props.product.price
                ? props.product.price.price.displayValue
                : '',
          },
        }}
        attributes={{ justifyContent: 'start', alignItems: 'start' }}
        selectedProduct={isProductSelected}
        onClickEventEmitter={() => {
          props.product.inStock &&
            handleProductSelection(
              props.product.sku,
              props.categoryId,
              props.setSelectedBundleProducts,
              props.maxProductsPerCategory,
              Number(props.product?.price?.price.amount),
              maxBundleQuantityReached,
              productsInCategory,
              isProductSelected,
              props.numberOfProductsInSelectedCategory,
              setSelectedQuantity,
            )
        }}
        notSelectable={!props.product.inStock}
        maxProductsSelected={
          maxBundleQuantityReached ||
          props.maxProductsPerCategory ===
            props.numberOfProductsInSelectedCategory
        }
        title={{ value: props.product.title, useAlternateStyle: false }}
        isProductSelected={isQuantitySelectorEnabled}
      />
      {!props.product.inStock && (
        <OutOfStock>
          <OutOfStockTitle>{i18nText.soldOutText}</OutOfStockTitle>
          <OutOfStockSubtitle>{i18nText.backInStockSoon}</OutOfStockSubtitle>
        </OutOfStock>
      )}
      {isProductSelected && (
        <IconContainer>
          <CloseIcon
            onClick={() =>
              removeProduct(props.product.sku, props.setSelectedBundleProducts)
            }
          />
        </IconContainer>
      )}
      {props.showInfoButton && (
        <InfoIconButton
          aria-label={i18nText.productInfoAriaLabel || 'Product Information'}
          onClick={() => setOpenInfoModal(true)}
        >
          <StyledIconInfo />
        </InfoIconButton>
      )}
      {openInfoModal && (
        <Modal
          open={openInfoModal}
          onClose={() => setOpenInfoModal(false)}
          i18nText={{
            closeLabel: i18nText.closeLabel,
            closeAriaLabel: i18nText.closeAriaLabel,
          }}
          gridColSpan={[12, 6, 8, 8]}
        >
          <React.Fragment>
            <Title>{i18nText.productInfoTitle}</Title>
            <SubTitle>{i18nText.productInfoUseAccordion}</SubTitle>
            <ProductDescriptionAccordion
              items={itemsWithTitle}
              details={{
                title: i18nText.productCategoryTitle,
                items: detailsWithTitle!,
              }}
            />
          </React.Fragment>
        </Modal>
      )}
      {isQuantitySelectorEnabled && props.maxProductsPerCategory > 1 && (
        <QuantityWrapper>
          <StyledQuantitySelector
            {...quantitySelector}
            quantityChangedCallback={(quantity) => {
              if (quantity) {
                handleUpdateQuantity(
                  props.product.sku,
                  quantity,
                  props.setSelectedBundleProducts,
                  setSelectedQuantity,
                )
              }
            }}
          />
        </QuantityWrapper>
      )}
    </div>
  )
}
